<script setup lang="ts">
  import { ref, onMounted, watch, computed } from 'vue';
  import '@typeform/embed/build/css/widget.css';
  import { createWidget } from '@typeform/embed';
  import { useVuelidate } from '@vuelidate/core';
  import { email, numeric, helpers } from '@vuelidate/validators';
  import * as Sentry from '@sentry/vue';

  import { Customer } from '@/models/responseMenu.interface';
  import { Profile } from '@/models/profile.interface';
  import { DataTrack } from '@/models/dataTrack.interface';
  import { dataNameRate, RATE_SERVICE, RateServices } from '@/models/rateMenu.interface';
  import { useLanguage } from "@/helpers/language-composables";
  import menuService from '@/services/menu';
  import { ResponseTypeform } from '@/models/responseTypeForm.interface';
  import { useMenuStore } from '@/store';

  const spaces = helpers.regex(/^[^\s]+/);

  const { getLabels } = useLanguage();
  const emit = defineEmits(['closeModalRate', 'hiddenButtonFloat', 'successfulSurveyResponse']);

  const { typeFormData } = useMenuStore();

  const props = defineProps<{
    customerData: Customer,
    profileData: Profile,
    trakingData: DataTrack,
    supplierId: number,
    flatRate: RateServices,
  }>();

  const numberQualite = ref<number>(0);
  const commentRate = ref<string>("");
  const formData = ref<Profile>({ name: "", phoneNumber: NaN, email: "" });
  const flatTypeForm = ref<boolean>(true);
  const errorQualification = ref<boolean>(false);
  const submitting = ref<boolean>(false);
  const submitted = ref<boolean>(false);
  const isChecked = ref<boolean>(true);

  const validations = computed(() => {
    return {
      name: { spaces },
      phoneNumber: { spaces, numeric },
      email: { spaces, email },
    }
  });

  watch(props, (val) => {
    if (val) initRate();
  });
  watch(numberQualite, (val) => {
    if (val > 0) errorQualification.value = false;
  });

  const v$ = useVuelidate(validations, formData);

  const closeRating = () => {
    submitting.value = false;
    numberQualite.value = 0;
    commentRate.value = "";
    formData.value = { name: "", phoneNumber: NaN, email: "" };
    closeModal();
  };
  const qualificationChoose = (item: number) => {
    numberQualite.value = item;
  };
  const setFormProfile = () => {
    if (!props.profileData) return;
    formData.value = {
      name: props.profileData?.name,
      phoneNumber: props.profileData?.phoneNumber,
      email: props.profileData?.email,
    };
  };
  const sumbitForm = async () => {
    try {
      submitting.value = true;
      if (numberQualite.value <= 0) {
        errorQualification.value = true;
        submitting.value = false;
        return;
      };
      if (!(await v$.value.$validate())) {
        submitting.value = false;
        return;
      };
      if (!Object.keys(RATE_SERVICE).includes(props.flatRate)) {
        closeRating();
        return;
      };
      const serviceResult = await menuService.SendRateOnTable({
        rate: numberQualite.value,
        service: RATE_SERVICE[props.flatRate],
        supplier_id: props.trakingData?.supplier_id,
        comment: commentRate.value,
        user_name: formData.value.name,
        user_phone: formData.value.phoneNumber,
        user_email: formData.value.email
      });
      if (serviceResult && isChecked.value) {
        handleSuccessSubmission();
        emit('successfulSurveyResponse', RATE_SERVICE[props.flatRate]);
      };
    } catch (exception) {
      Sentry.captureException(exception);
      submitting.value = false;
    }
  };
  const handleSuccessSubmission = () => {
    closeRating();
    submitted.value = true;
  };
  const initRate = () => {
    if (!typeFormData || Object.keys(typeFormData).length === 0) {
      flatTypeForm.value = false;
      return;
    };

    let hiddenObj = {};

    if (props.trakingData?.supplier_id && props.trakingData?.session_id) {
      hiddenObj = {
        supplier_id: props.trakingData?.supplier_id,
        session_id: props.trakingData?.session_id
      };
    };

    const options = {
      container: document.querySelector('#form') as HTMLDivElement,
      autoClose: true,
      onSubmit: closeModal,
      onClose: closeModal,
      autoResize: true,
      inlineOnMobile: true,
      hidden: hiddenObj
    };

    createWidget(typeFormData.form_id, options);
  };
  const closeModal = () => {
    emit('closeModalRate');
  };
  const openPrivacyPolicies = () => {
    window.open('/politica-privacidad-datos.pdf', '_blank');
  };

  onMounted(() => {
    setFormProfile();
    initRate();
  });
</script>

<template>
    <div class="submitted" v-if="submitted">{{ getLabels('RATEMENU.THANKMESSAGE') }}</div>
    <div id="form" v-else-if="flatTypeForm"></div>
    <div v-else data-test-id="rating-form-not-flat">
      <div v-if="props.flatRate === dataNameRate.RATENPS" class="modal-rateTit">
        {{ getLabels('RATEMENU.RATEMENULABEL2') }} {{ props.customerData?.name }}
      </div>
      <div v-if="props.flatRate === dataNameRate.RATEDIGITAL" class="modal-rateTit">
        {{ getLabels('RATEMENU.RATEMENULABEL') }}
      </div>
      <hr>
      <div v-if="props.flatRate === dataNameRate.RATENPS" class="modal-txt">
        {{ getLabels('RATEMENU.RATEMENUSUBTITLELABEL2') }}
      </div>
      <div v-if="props.flatRate === dataNameRate.RATEDIGITAL" class="modal-txt">
        {{ getLabels('RATEMENU.RATEMENUSUBTITLELABEL') }}
      </div>
      <div class="modal-wrapRating">
        <div v-for="item in 10" :key="item" class="modal-btnRating"
          :class="{ 'modal-btnRating modal-btnRating__selected': item <= numberQualite }" v-text="item"
          @click="qualificationChoose(item)"></div>
      </div>
      <div class="modal-wrapRating">
        <div class="modal-txt">{{ getLabels('RATEMENU.UNSATISFIED') }}</div>
        <div class="modal-txt">{{ getLabels('RATEMENU.SATISFIED') }}</div>
      </div>
      <small v-if="errorQualification" class="error-txt">{{ getLabels('RATEMENU.RATEMENUQUALIFICATIONERROR') }}</small>
      <div v-if="props.flatRate === dataNameRate.RATENPS">
        <div class="modal-txt">{{ getLabels('RATEMENU.RATEMENUQUESTIONTEXT') }}</div>
        <div class="modal-wrapInput">
          <textarea v-model="commentRate" :placeholder="getLabels('RATEMENU.READLABEL')"></textarea>
        </div>
      </div>
      <div :class="v$.name.$error ? 'modal-wrapInput error' : 'modal-wrapInput'">
        <input v-model="formData.name" type="text" :placeholder="getLabels('FORMLABELS.NAME')" />
      </div>
      <small v-for="error in v$.name.$errors" :key="error.$uid" class="error-txt">
        {{ getLabels('FORMLABELS.NAMEERROR') }}
      </small>
      <div :class="v$.phoneNumber.$error ? 'modal-wrapInput error' : 'modal-wrapInput'">
        <input v-model="formData.phoneNumber" type="number" :placeholder="getLabels('FORMLABELS.PHONENUMBER')" />
      </div>
      <small v-for="error in v$.phoneNumber.$errors" :key="error.$uid" class="error-txt">
        {{ getLabels('FORMLABELS.PHONEERROR') }}
      </small>
      <div :class="v$.email.$error ? 'modal-wrapInput error' : 'modal-wrapInput'">
        <input v-model="formData.email" type="text" :placeholder="getLabels('FORMLABELS.EMAIL')" />
      </div>
      <small v-if="v$.email.$error" class="error-txt">{{ getLabels('FORMLABELS.EMAILERROR') }}</small>
      <div class="custom-checkbox">
        <input v-model="isChecked" type="checkbox" id="privacy-policy" />
        <span @click="openPrivacyPolicies()" v-html="getLabels('PRIVACYPOLICYCONSENT')" />
      </div>
      <button class="modal-btnMain" data-test-id="rating-form-submit" :disabled="submitting" @click="sumbitForm()">
        <svg v-if="submitting" style="width: 20px;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200"><radialGradient id="a12" cx=".66" fx=".66" cy=".3125" fy=".3125" gradientTransform="scale(1.5)"><stop offset="0" stop-color="#FFFFFF"></stop><stop offset=".3" stop-color="#FFFFFF" stop-opacity=".9"></stop><stop offset=".6" stop-color="#FFFFFF" stop-opacity=".6"></stop><stop offset=".8" stop-color="#FFFFFF" stop-opacity=".3"></stop><stop offset="1" stop-color="#FFFFFF" stop-opacity="0"></stop></radialGradient><circle transform-origin="center" fill="none" stroke="url(#a12)" stroke-width="20" stroke-linecap="round" stroke-dasharray="200 1000" stroke-dashoffset="0" cx="100" cy="100" r="70"><animateTransform type="rotate" attributeName="transform" calcMode="spline" dur="2" values="360;0" keyTimes="0;1" keySplines="0 0 1 1" repeatCount="indefinite"></animateTransform></circle><circle transform-origin="center" fill="none" opacity=".2" stroke="#FFFFFF" stroke-width="20" stroke-linecap="round" cx="100" cy="100" r="70"></circle></svg>
        <span v-else>{{ getLabels('RATEMENU.RATEQUALIFICATIONBUTTON') }}</span>

      </button>
    </div>
</template>

<style scoped lang="scss">
  .modal-inner-typeform {
    background-color: #fff0 !important;
    max-height: inherit !important;

    #form {
      height: 75vh !important;
    }
  }

  .modal-btnClose {
    margin: 28px auto;
  }

  .submitted {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .custom-checkbox {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 1rem 0rem 1rem 0rem;

    input[type="checkbox"] {
      appearance: none;
      width: 20px;
      height: 20px;
      border-radius: 4px;
      background-color: #fff;
      cursor: pointer;
      display: grid;
      place-content: center;

      &:checked {
        background-color: #535bf2;
      }
      &:checked::before {
        content: '✔';
        color: #fff;
        font-size: 14px;
      }
    }

    span {
      cursor: default;
      font-size: 1rem;
    }
  }
</style>
